import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchExpenses, failureCode, type ExpensesView } from "../services/ccTenantViewService";
import { Card, Kpi, PageHeader, StateBox, money } from "../components/ui";

// Gastos que Coding Cloud atribuyó a ESTE cliente (CC-CONSOLE #980 / #981):
// totales por mes y categoría y la lista de gastos confirmados. Solo lectura.

const RANGES = [3, 6, 12] as const;
const monthsAgo = (n: number) => {
  const d = new Date(Date.now() - 6 * 3600 * 1000);
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - n);
  return d.toISOString().slice(0, 7);
};

export default function ExpensesClientView() {
  const { t } = useTranslation("common");
  const [months, setMonths] = useState<(typeof RANGES)[number]>(6);
  const [view, setView] = useState<ExpensesView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Solo la última petición pinta: cambiar de rango rápido no deja datos viejos.
  const requestId = useRef(0);
  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const next = await fetchExpenses(monthsAgo(months - 1), monthsAgo(0));
      if (id === requestId.current) setView(next);
    } catch (err) {
      if (id !== requestId.current) return;
      setView(null);
      setError(failureCode(err) ?? "generic");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [months]);

  useEffect(() => {
    void load();
  }, [load]);

  const cur = view?.baseCurrency ?? "MXN";
  const byCategory = new Map<string, number>();
  for (const m of view?.months ?? []) for (const [c, v] of Object.entries(m.byCategory)) byCategory.set(c, (byCategory.get(c) ?? 0) + v);

  return (
    <div className="w-full max-w-6xl space-y-4 overflow-y-auto p-4 sm:p-6">
      <PageHeader title={t("clientView.expenses.title")} subtitle={t("clientView.expenses.subtitle", { client: view?.client.name ?? "" })}>
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setMonths(r)}
            className={`rounded-lg px-3 py-2 text-xs font-medium ${months === r ? "bg-primary-500 text-on-primary" : "border border-line text-content-secondary hover:bg-subtle"}`}
          >
            {t("clientView.lastMonths", { count: r })}
          </button>
        ))}
      </PageHeader>
      <StateBox loading={loading} error={error} onRetry={() => void load()} />

      {view && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <Kpi label={t("clientView.expenses.total")} value={money(view.total, cur)} />
            <Kpi label={t("clientView.expenses.count")} value={String(view.expenses.length)} />
            <Kpi label={t("clientView.expenses.categories")} value={String(byCategory.size)} />
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card title={t("clientView.expenses.byMonth")}>
              <table className="w-full text-sm">
                <tbody>
                  {view.months.map((m) => (
                    <tr key={m.period} className="border-t border-line text-content first:border-t-0">
                      <td className="py-1.5">{m.period}</td>
                      <td className="py-1.5 text-right tabular-nums">{money(m.total, cur)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <Card title={t("clientView.expenses.byCategory")}>
              {byCategory.size === 0 ? (
                <p className="text-xs text-content-secondary">{t("clientView.empty")}</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {[...byCategory.entries()]
                      .sort((a, b) => b[1] - a[1])
                      .map(([c, v]) => (
                        <tr key={c} className="border-t border-line text-content first:border-t-0">
                          <td className="py-1.5">{t(`clientView.categories.${c}`, { defaultValue: c })}</td>
                          <td className="py-1.5 text-right tabular-nums">{money(v, cur)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>
          <Card title={t("clientView.expenses.list")}>
            {view.expenses.length === 0 ? (
              <p className="text-xs text-content-secondary">{t("clientView.empty")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-content-secondary">
                      <th className="py-1">{t("clientView.expenses.date")}</th>
                      <th className="py-1">{t("clientView.expenses.vendor")}</th>
                      <th className="py-1">{t("clientView.expenses.description")}</th>
                      <th className="py-1 text-right">{t("clientView.expenses.amount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.expenses.map((e) => (
                      <tr key={e.id} className="border-t border-line text-content">
                        <td className="py-1.5 whitespace-nowrap">{e.date}</td>
                        <td className="py-1.5">{e.vendor}</td>
                        <td className="py-1.5 text-xs">{e.description}</td>
                        <td className="py-1.5 text-right tabular-nums whitespace-nowrap">
                          {money(e.amount, e.currency)}
                          {e.currency !== cur && e.amountBase != null && <span className="block text-[11px] text-content-secondary">{money(e.amountBase, cur)}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
